import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaMapMarkerAlt, FaEdit } from 'react-icons/fa';
import API_CONFIG from './config';
import { useRef } from 'react';
import './styles/Projects.css';
import { set } from 'date-fns';
import { se } from 'date-fns/locale';

interface Site {
    site_id: string;
    site_name: string;
    site_address?: string;
    display_address?: string;
    is_active?: boolean;
    ticket_count?: number;
    external?: number;
    client?: string;
    email?: string;
    tip_reference_number?: string;
}

const CARD_WIDTH = 350;
const CARD_HEIGHT = 340;

interface ProjectsProps {
    user: { job_role: string; name: string; user_id: number; site_ids?: string[] };
}

const Projects: React.FC<ProjectsProps> = ({ user }) => {
    const navigate = useNavigate();
    const [sites, setSites] = useState<Site[]>([]);
    const [checkingSession, setCheckingSession] = useState(true);
    const [gdrNumber, setGdrNumber] = useState('');
    const [siteName, setSiteName] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editSite, setEditSite] = useState<Site | null>(null);
    const [editName, setEditName] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editActive, setEditActive] = useState(true);
    const [postcode, setPostcode] = useState('');
    const [cityTown, setCityTown] = useState('');
    const [borough, setBorough] = useState('');
    const [county, setCounty] = useState('');
    const [isExternal, setIsExternal] = useState(false);
    const [client, setClient] = useState('');
    const [externalSiteId, setExternalSiteId] = useState('');
    const [tipReferenceNumber, setTipReferenceNumber] = useState('');
    const [editClient, setEditClient] = useState('');
    const [editTipReferenceNumber, setEditTipReferenceNumber] = useState('');
    const [editIsExternal, setEditIsExternal] = useState(false);
    const [email, setEmail] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [addressSuggestions, setAddressSuggestions] = useState<{ address: string; latitude: number; longitude: number }[]>([]);
    const [selectedAddressObj, setSelectedAddressObj] = useState<{ address: string; latitude: number; longitude: number } | null>(null);
    const [showAddressDropdown, setShowAddressDropdown] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [fullAddress, setFullAddress] = useState('');
    const [activeSuggestion, setActiveSuggestion] = useState<number>(-1);
    const [displayAddress, setDisplayAddress] = useState('');
    const [editDisplayAddress, setEditDisplayAddress] = useState('');

    // New state for site/ticket analysis
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [siteTickets, setSiteTickets] = useState<any[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<any | null>(null);

    const measureApiCall = async (name: string, apiCall: () => Promise<any>) => {
        const startTime = performance.now();
        try {
            const result = await apiCall();
            const endTime = performance.now();
            const duration = endTime - startTime;
            
            if (duration > 500) {
                console.warn(`🐌 SLOW API CALL: ${name} took ${duration.toFixed(2)}ms`);
            } else {
                console.log(`⚡ API CALL: ${name} took ${duration.toFixed(2)}ms`);
            }
            
            return result;
        } catch (error) {
            const endTime = performance.now();
            const duration = endTime - startTime;
            console.error(`❌ FAILED API CALL: ${name} failed after ${duration.toFixed(2)}ms`, error);
            throw error;
        }
    };

    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        const loadData = async () => {
            try {
                // Measure session check
                await measureApiCall('Session Check', () => 
                    axios.get('/api/session', { withCredentials: true })
                );
                
                // Measure sites loading
                const sitesResponse = await measureApiCall('Load Sites', () =>
                    axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
                );
                
                if (cancelled) return;
                
                // Sort sites (this is fast, no need to measure)
                const sortedSites = [...sitesResponse.data].sort((a: Site, b: Site) => {
                    if ((a.external ?? 0) !== (b.external ?? 0)) {
                        return (a.external ?? 0) - (b.external ?? 0);
                    }
                    const regex = /GDR(\d+)(?:\((\w)\))?/;
                    const matchA = String(a.site_id).match(regex);
                    const matchB = String(b.site_id).match(regex);

                    const numA = matchA ? parseInt(matchA[1], 10) : Number.MAX_SAFE_INTEGER;
                    const numB = matchB ? parseInt(matchB[1], 10) : Number.MAX_SAFE_INTEGER;

                    if (numA !== numB) return numA - numB;

                    const letterA = matchA && matchA[2] ? matchA[2] : '';
                    const letterB = matchB && matchB[2] ? matchB[2] : '';
                    return letterA.localeCompare(letterB);
                });
                
                setSites(sortedSites);
                
            } catch (err: any) {
                if (cancelled) return;
                if (err?.response?.status === 401) {
                    navigate('/login');
                } else {
                    setSites([]);
                }
            } finally {
                if (!cancelled) setCheckingSession(false);
            }
        };

        loadData();
        return () => { cancelled = true; };
    }, [showDialog, message, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');

        // Validation logic
        if (!siteName.trim()) {
            setError('Site Name is required.');
            return;
        }
        if (!selectedAddressObj) {
            setError('Please select a valid address from the suggestions.');
            return;
        }
        if (isExternal) {
            if (!client.trim()) {
                setError('Client Name is required for an external site.');
                return;
            }
        } else {
            if (!gdrNumber.trim()) {
                setError('GDR Number is required for an internal site.');
                return;
            }
        }

        const site_id = isExternal ? '' : `GDR${gdrNumber.trim()}`;
        const site_name = siteName.trim();
        const site_address = selectedAddressObj?.address || fullAddress;
        const display_address = displayAddress.trim();
        const latitude = selectedAddressObj?.latitude ?? null;
        const longitude = selectedAddressObj?.longitude ?? null;

        const payload = {
            site_id,
            site_name,
            site_address,
            display_address,
            is_active: isActive ? 1 : 0,
            external: isExternal ? 1 : 0,
            client: isExternal ? client : '',
            tip_reference_number: isExternal ? tipReferenceNumber : '',
            email: isExternal ? email : null,
            latitude,
            longitude
        };
        console.log('Submitting site payload:', payload);

        try {
            await axios.post('/api/sites', payload, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setMessage('Site created!');
            setGdrNumber('');
            setSiteName('');
            setPostcode('');
            setCityTown('');
            setBorough('');
            setCounty('');
            setDisplayAddress('');
            setIsActive(true);
            setIsExternal(false);
            setClient('');
            setShowDialog(false);
            // reload sites
            const res = await axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setSites(res.data);
        } catch (err: any) {
            if (err?.response?.status === 401) return navigate('/login');
            setError(err?.response?.data?.error || 'Failed to create site');
        }
    };

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFullAddress(e.target.value);
        setSelectedAddressObj(null);
        setActiveSuggestion(-1);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        // Only fetch if address is at least 4 characters
        if (e.target.value.length >= 4) {
            debounceTimeout.current = setTimeout(() => {
                fetchAddressesByAddress(e.target.value);
            }, 1000); // 1 second debounce
        } else {
            setAddressSuggestions([]);
            setShowAddressDropdown(false);
        }
    };

    const fetchAddressesByAddress = async (address: string) => {
        if (!address) return;
        try {
            const res = await axios.get(
                `/api/addresses-by-address?address=${encodeURIComponent(address)}`,
                { withCredentials: true, baseURL: API_CONFIG.BASE_URL }
            );
            setAddressSuggestions(res.data || []);
            setShowAddressDropdown(true);
        } catch {
            setAddressSuggestions([]);
            setShowAddressDropdown(false);
        }
    };

    const handleEditClick = (site: Site) => {
        setEditSite(site);
        setEditName(site.site_name);
        setEditAddress(site.site_address || '');
        setEditDisplayAddress(site.display_address || '');
        setEditActive(site.is_active ?? true);
        setEditIsExternal(Number(site.external) === 1);
        setEditClient(site.client || '');
        setEditTipReferenceNumber(site.tip_reference_number || '');
        setEditEmail(site.email || '');
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (!editSite) return;
        try {
            await axios.put(`/api/sites/${editSite.site_id}`, {
                site_name: editName,
                site_address: editAddress,
                display_address: editDisplayAddress,
                is_active: editActive,
                external: editIsExternal ? 1 : 0,
                client: editIsExternal ? editClient : '',
                tip_reference_number: editIsExternal ? editTipReferenceNumber : '',
                email: editIsExternal ? editEmail : ''
            }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setMessage('Site updated!');
            setEditDialogOpen(false);
            setEditSite(null);
            const res = await axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setSites(res.data);
        } catch (err: any) {
            if (err?.response?.status === 401) return navigate('/login');
            setError(err?.response?.data?.error || 'Failed to update site');
        }
    };

    const handleSiteClick = (site: Site) => {
        setSelectedSite(site);
        setSelectedTicket(null);
        axios.get(`/api/waste-tickets?site_id=${site.site_id}`, { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
            .then(res => setSiteTickets(res.data))
            .catch(err => {
                if (err?.response?.status === 401) return navigate('/login');
                setSiteTickets([]);
            });
    };

    useEffect(() => {
        if (showDialog) {
            setGdrNumber('');
            setSiteName('');
            setDisplayAddress('');
            setPostcode('');
            setCityTown('');
            setBorough('');
            setCounty('');
            setIsActive(true);
            setIsExternal(false);
            setClient('');
            setTipReferenceNumber('');
            setEmail('');
            setMessage('');
            setError('');
        }
    }, [showDialog]);

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                background: '#f5f6fa', // Keep this as the main background
                display: 'flex',
                flexDirection: 'column',
                padding: '40px',
                boxSizing: 'border-box'
            }}
            className="projects-main-container"
        >
        {/* Header directly on gray background */}
        <div className="projects-header">
            <div className="projects-title">
                <h2 style={{
                    margin: 0,
                    fontSize: '2.4em',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: '#333' // Make sure text is visible on gray background
                }}>
                    Project Sites
                </h2>
            </div>
            <div className="create-button-container">
                {user.job_role === 'Admin' && (
                <button
                    style={{
                        background: '#115740',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '10px 22px',
                        fontWeight: 600,
                        fontSize: '1em',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(17,87,64,0.3)'
                    }}
                    onClick={() => setShowDialog(true)}
                >
                    + Create Site
                </button>
                )}
            </div>
        </div>
        
        {/* Grid directly on gray background */}
        <div className="projects-grid">
            {sites.length === 0 ? (
                <div style={{ 
                    color: '#666', 
                    textAlign: 'center', 
                    padding: 32, 
                    width: '100%', 
                    gridColumn: '1/-1',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                    No project sites found.
                </div>
            ) : (
                sites.map((site, idx) => (
                    <div
                        key={site.site_id}
                        className="project-card"
                        style={{
                            background: '#fff', // White cards on gray background
                            borderRadius: 12,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', // Slightly stronger shadow for contrast
                            padding: 28,
                            width: CARD_WIDTH,
                            height: CARD_HEIGHT,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleSiteClick(site)}
                    >
                        <div 
                            className="card-avatar"
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: '#115740',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '1.2em',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'absolute',
                                top: 22,
                                left: 22
                            }}
                        >
                            {site.site_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
                        </div>
                        <div style={{
                            position: 'absolute',
                            top: 22,
                            right: 22,
                            display: 'flex',
                            gap: 16
                        }}>
                            {user.job_role === 'Admin' && (
                                <FaEdit
                                    className="card-edit-icon"
                                    style={{ color: '#888', cursor: 'pointer', fontSize: '1.1em' }}
                                    title="Edit"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleEditClick(site);
                                    }}
                                />
                            )}
                        </div>
                        <div style={{
                            width: '90%',
                            margin: '0 auto',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            justifyContent: 'center'
                        }}>
                            <div 
                                className="card-title"
                                style={{
                                    fontWeight: 700,
                                    fontSize: '1.1em',
                                    color: '#115740',
                                    marginTop: 54,
                                    marginBottom: 10,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                    overflow: 'visible',
                                    textOverflow: 'unset'
                                }}
                            >
                                {site.site_id} - {site.site_name}
                            </div>
                            <div 
                                className="card-address"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 10,
                                    color: '#444',
                                    fontSize: '0.9em',
                                    marginBottom: 10,
                                    width: '100%',
                                }}
                            >
                                <FaMapMarkerAlt
                                    style={{
                                        color: '#115740',
                                        fontSize: '1.1em',
                                        flexShrink: 0,
                                        alignSelf: 'flex-start',
                                        marginTop: 2
                                    }}
                                />
                                <span style={{
                                    textAlign: 'center',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-line',
                                    width: '100%',
                                    display: 'block'
                                }}>
                                    {site.display_address}
                                </span>
                            </div>
                            <div 
                                className="card-status"
                                style={{
                                    color: site.is_active ? '#115740' : '#d32f2f',
                                    fontWeight: 600,
                                    fontSize: '1em',
                                    marginTop: 10,
                                    marginBottom: 10
                                }}
                            >
                                {site.is_active ? 'Active' : 'Inactive'}
                            </div>
                            <hr style={{ width: '70%', margin: '18px auto 12px auto', border: 'none', borderTop: '1px solid #eee' }} />
                            <div 
                                className="card-ticket-count"
                                style={{
                                    fontSize: '1em',
                                    color: '#444',
                                    marginTop: 10
                                }}
                            >
                                Waste tickets associated: <span style={{ fontWeight: 700 }}>{site.ticket_count ?? 0}</span>
                                {site.external === 1 && (
                                    <>
                                        {site.client && (
                                            <div style={{ marginTop: 6 }}>
                                                Client: <span style={{ fontWeight: 700 }}>{site.client}</span>
                                            </div>
                                        )}
                                        {site.tip_reference_number && (
                                            <div style={{ marginTop: 6 }}>
                                                Tip Reference Number: <span style={{ fontWeight: 700 }}>{site.tip_reference_number}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>

            {/* Ticket List Modal */}
            {selectedSite && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 2000
                }}>
                    <div style={{
                        background: '#fff', padding: 32, borderRadius: 12, minWidth: 400, maxWidth: 700, position: 'relative'
                    }}>
                        <button
                            style={{
                                position: 'absolute', top: 18, right: 18, fontSize: '1.5em', background: 'none', border: 'none', color: '#888', cursor: 'pointer'
                            }}
                            onClick={() => { setSelectedSite(null); setSiteTickets([]); setSelectedTicket(null); }}
                            aria-label="Close"
                        >×</button>
                        <h3 style={{ marginBottom: 18 }}>Waste Tickets for {selectedSite.site_id} - {selectedSite.site_name}</h3>
                        {siteTickets.length === 0 ? (
                            <div>No tickets found.</div>
                        ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {siteTickets.slice(0, 5).map(ticket => (
                                    <li key={ticket.waste_ticket_id} style={{ marginBottom: 16, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
                                        <button
                                            style={{
                                                background: 'none', border: 'none', color: '#115740', fontWeight: 600, fontSize: '1em', cursor: 'pointer', textAlign: 'left'
                                            }}
                                            onClick={() => setSelectedTicket(ticket)}
                                        >
                                            {ticket.waste_ticket_id}: {ticket.waste_description} ({ticket.date})
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {/* Ticket Details Modal */}
            {selectedTicket && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 2100
                }}>
                    <div style={{
                        background: '#fff', padding: 32, borderRadius: 12, minWidth: 400, maxWidth: 700, position: 'relative'
                    }}>
                        <button
                            style={{
                                position: 'absolute', top: 18, right: 18, fontSize: '1.5em', background: 'none', border: 'none', color: '#888', cursor: 'pointer'
                            }}
                            onClick={() => setSelectedTicket(null)}
                            aria-label="Close"
                        >×</button>
                        <h3 style={{ marginBottom: 18 }}>Waste Ticket {selectedTicket.waste_ticket_id}</h3>
                        <div><strong>Description:</strong> {selectedTicket.description}</div>
                        <div><strong>Collection Date:</strong> {selectedTicket.collection_datetime}</div>
                        <div><strong>Site:</strong> {selectedTicket.site_name} ({selectedTicket.display_address})</div>
                        <div><strong>Dropoff:</strong> {selectedTicket.dropoff_name} ({selectedTicket.dropoff_display_address})</div>
                        <div><strong>Vehicle:</strong> {selectedTicket.vehicle}</div>
                        <div><strong>Driver:</strong> {selectedTicket.driver_name}</div>
                    </div>
                </div>
            )}
            {/* Edit Dialog */}
            {editDialogOpen && editSite && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="project-edit-dialog" style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Edit Site</h3>
                        <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <input
                                type="text"
                                placeholder="Site Name"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Display Address"
                                value={editDisplayAddress}
                                onChange={e => setEditDisplayAddress(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Site Address"
                                value={editAddress}
                                onChange={e => setEditAddress(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            {editIsExternal && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Client Name"
                                        value={editClient}
                                        onChange={e => setEditClient(e.target.value)}
                                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Tip Reference Number"
                                        value={editTipReferenceNumber}
                                        onChange={e => setEditTipReferenceNumber(e.target.value)}
                                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                    <input
                                        type="email"
                                        placeholder="Client Email Address"
                                        value={editEmail}
                                        onChange={e => setEditEmail(e.target.value)}
                                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                </>
                            )}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={editActive}
                                    onChange={e => setEditActive(e.target.checked)}
                                />
                                Active
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={editIsExternal}
                                    onChange={e => setEditIsExternal(e.target.checked)}
                                />
                                External site
                            </label>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#115740',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 600
                                    }}
                                    onClick={handleEditSave}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#eee',
                                        color: '#333',
                                        border: 'none',
                                        fontWeight: 500
                                    }}
                                    onClick={() => {
                                        setEditDialogOpen(false);
                                        setEditSite(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}
            {/* Create Dialog */}
            {showDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="project-dialog" style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320, position: 'relative'
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Create Project Site</h3>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {/* Only show GDR Number if not external */}
                            {!isExternal && (
                                <div className="gdr-input-row">
                                    <span className="gdr-prefix">GDR</span>
                                    <input
                                        type="text"
                                        placeholder="Number"
                                        value={gdrNumber}
                                        onChange={e => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            setGdrNumber(val);
                                        }}
                                        className="gdr-number-input"
                                        required
                                    />
                                </div>
                            )}
                            {isExternal && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Client Name"
                                        value={client}
                                        onChange={e => setClient(e.target.value)}
                                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="Tip Reference Number"
                                        value={tipReferenceNumber}
                                        onChange={e => setTipReferenceNumber(e.target.value)}
                                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                    <input
                                        type="email"
                                        placeholder="Client Email Address"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                </>
                            )}
                            <input
                                type="text"
                                placeholder="Site Name"
                                value={siteName}
                                onChange={e => setSiteName(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Display Address"
                                value={displayAddress}
                                onChange={e => setDisplayAddress(e.target.value)}
                                required
                            />
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="Full Address"
                                    value={fullAddress}
                                    onChange={handleAddressChange}
                                    onFocus={() => {
                                        if (addressSuggestions.length > 0) setShowAddressDropdown(true);
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setShowAddressDropdown(false), 150);
                                    }}
                                    onKeyDown={e => {
                                        if (addressSuggestions.length > 0) {
                                            if (e.key === 'ArrowDown') {
                                                setActiveSuggestion(prev => Math.min(prev + 1, addressSuggestions.length - 1));
                                            } else if (e.key === 'ArrowUp') {
                                                setActiveSuggestion(prev => Math.max(prev - 1, 0));
                                            } else if (e.key === 'Enter' && activeSuggestion >= 0) {
                                                const selected = addressSuggestions[activeSuggestion];
                                                setSelectedAddressObj(selected);
                                                setFullAddress(selected.address);
                                                setShowAddressDropdown(false);
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                    style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                                    required
                                    autoComplete="off"
                                />
                                {showAddressDropdown && addressSuggestions.length > 0 && (
                                    <ul
                                        style={{
                                            listStyle: 'none',
                                            margin: 0,
                                            padding: 0,
                                            position: 'absolute',
                                            background: '#fff',
                                            border: '1px solid #ccc',
                                            borderRadius: 6,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                            zIndex: 1001,
                                            width: '100%',
                                            maxHeight: 180,
                                            overflowY: 'auto',
                                            top: 44, // below the input
                                            left: 0
                                        }}
                                    >
                                        {addressSuggestions.map((suggestion, idx) => (
                                            <li
                                                key={suggestion.address}
                                                style={{
                                                    padding: '10px 16px',
                                                    background: idx === activeSuggestion ? '#e6f2ed' : '#fff',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={() => setActiveSuggestion(idx)}
                                                onMouseLeave={() => setActiveSuggestion(-1)}
                                                onMouseDown={() => {
                                                    setSelectedAddressObj(suggestion);
                                                    setFullAddress(suggestion.address);
                                                }}
                                                onMouseUp={() => setShowAddressDropdown(false)}
                                            >
                                                {suggestion.address}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div 
                                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                onClick={() => setIsActive(!isActive)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={e => setIsActive(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                Active
                            </div>
                            <div
                                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                onClick={() => setIsExternal(!isExternal)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isExternal}
                                    onChange={e => setIsExternal(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                External site
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: selectedAddressObj ? '#115740' : '#ccc',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 600,
                                        cursor: selectedAddressObj ? 'pointer' : 'not-allowed'
                                    }}
                                    disabled={!selectedAddressObj}
                                >
                                    Create Site
                                </button>
                                <button type="button" style={{
                                    padding: '10px 22px',
                                    borderRadius: 6,
                                    background: '#eee',
                                    color: '#333',
                                    border: 'none',
                                    fontWeight: 500
                                }} onClick={() => setShowDialog(false)}>
                                    Cancel
                                </button>
                            </div>
                            {message && <div style={{ color: '#115740', fontWeight: 500 }}>{message}</div>}
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Projects;